import React, {useCallback, useEffect, useState} from "react";
import {
    Box,
    Card,
    CardActionArea, CardActions, CardContent, CardMedia,
    Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Drawer, IconButton,
    ListItem,
    ListItemAvatar, ListItemIcon,
    ListItemText, Select, Tabs, TextField,
    Typography, useMediaQuery
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {LoadingButton, Skeleton} from "@mui/lab";
import {StorageService} from "../../services/StorageService";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {ProgramService} from "../../services/ProgramService";
import {getSupportedLocale, LOCALE_DE} from "../../utils/locales";
import CircleIcon from '@mui/icons-material/Circle';
import dayjs from "dayjs";
import Tab from "@mui/material/Tab";
import { CreditCard, CalendarMonth} from "@mui/icons-material";
import {loadStripe} from "@stripe/stripe-js";
import {TransactionsService} from "../../services/TransactionsService";
import {EmbeddedCheckoutProvider, EmbeddedCheckout} from "@stripe/react-stripe-js";
import {displayAlertDialog} from "../../utils/alertDialogs";
import {UserClassPassService} from "../../services/UserClassPassService";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Calendar, momentLocalizer} from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {BookingsService} from "../../services/BookingsService";
import Vimeo from "@u-wave/react-vimeo";
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import {
    trackBookingExistsEvent,
    trackBookingInsufficientClassPassesEvent, trackBookingProgramSubscriptionOnHoldEvent, trackBookingSuccessfulEvent,
    trackButtonClickEvent, trackClassIntroVideoPlayedEvent,
    trackModalViewEvent,
    trackPageViewEvent, trackPurchaseInitiatedEvent, trackPurchaseSuccessfulEvent,
    trackRequestFailedEvent, trackVideoErrorEvent
} from "../../utils/tracking";
import Link from "@mui/material/Link";
import {log} from "../../utils/logger";
import {
    PRICING_TYPE_INSTALMENTAL,
    PRICING_TYPE_ONE_TIME,
    PROGRAM_TYPE_DROP_IN,
    PROGRAM_TYPE_FIXED_COURSE
} from "../../utils/constants";



const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const localizer = momentLocalizer(moment);

const Program = () => {

    const {t} = useTranslation();

    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate();
    const location = useLocation();

    //selected schedule from schedule pages
    const [locationSelectedSchedule, setLocationSelectedSchedule] = useState(location.state);

    const [program, setProgram] = useState(null);
    const [programPricing, setProgramPricing] = useState([]);
    const [programSchedule, setProgramSchedule] = useState([]);
    const [calendarSchedules, setCalendarSchedules] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState();
    const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
    const [isBuyClassPassDialogOpen, setIsBuyClassPassDialogOpen] = useState(false)
    const [isStripeCheckoutDialogOpen, setIsStripeCheckoutDialogOpen] = useState(false)
    const [isBookingClass, setIsBookingClass] = useState(false);
    const [availableClassPasses, setAvailableClassPasses] = useState('');

    const [selectedProgramPrice, setSelectedProgramPrice] = useState();

    const [isLoading, setIsLoading] = useState(false);

    const user = StorageService.loadUser();
    const appLocale = StorageService.loadAppLocale();

    const [tabValue, setTabValue] = useState(0);

    const [quantity, setQuantity] = useState(1);
    const [selectedProgramPriceTotalPrice, setSelectedProgramPriceTotalPrice] = useState(0);
    const [stripeCheckoutOptions, setStripeCheckoutOptions] = useState({clientSecret: ""});
    const [isPurchasing, setIsPurchasing] = useState();

    const isNotMobile = useMediaQuery('(min-width:1200px)');
    const scrollCalendarTo12PM = new Date(new Date().setUTCHours(12));


    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const goToLogin = () => {
        navigate('/login');
    }

    useEffect(
        async ()  => {
            trackPageViewEvent('class', `/classes/${id}`);

            await getProgram();

            const stripeCheckoutSessionId = searchParams.get("session_id");
            if (stripeCheckoutSessionId) {
                await fetchPurchaseClassPassTransactionStatus(stripeCheckoutSessionId);
            }
        },
        [],
    );

    useEffect(
        async ()  => {
            await getAvailableClassPasses();
            await getProgramPricing();
            await getProgramSchedule();

            if(locationSelectedSchedule) {
                handleSelectSchedule(locationSelectedSchedule);
                window.history.replaceState({}, '')
            }
        },
        [program],
    );

    useEffect(() => {
        if(quantity < 1 || isNaN(quantity)){
            return;
        }
        setSelectedProgramPriceTotalPrice((selectedProgramPrice?.totalAmount * quantity).toFixed(2));
    }, [quantity, selectedProgramPrice?.totalAmount]);

    const handleQuantityChange = (event) => {
        // const newQuantity = Math.max(1, parseInt(event.target.value) || 1);
        const newQuantity = parseInt(event.target.value);

        setQuantity(newQuantity);
    };


    const displayScheduleDialog = (shouldDisplay) => {
        if(shouldDisplay) {
            trackModalViewEvent(`Book class modal: ${program?.name}`)
        }

        setIsScheduleDialogOpen(shouldDisplay);
    }

    const onCloseBuyClassPassDialog = () => {
        trackButtonClickEvent(`cancel buy class pass: ${program?.name}`)
        setIsBuyClassPassDialogOpen(false);
    }

    const onCloseStripeCheckoutDialog = () => {
        trackButtonClickEvent(`cancel class pass stripe checkout: ${program?.name}`)
        setIsStripeCheckoutDialogOpen(false);
        window.location.reload();
    }

    const openBuyClassPassDialog = (programPrice) => {
        setSelectedProgramPrice(programPrice);
        setQuantity(1);
        setSelectedProgramPriceTotalPrice(programPrice.totalAmount * quantity);

        trackModalViewEvent(`Buy class pass modal: ${program?.name}`)
        setIsBuyClassPassDialogOpen(true);
    }

    const getProgram = async () => {
        setIsLoading(true);

        const response =  await ProgramService.getProgram(id, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        setIsLoading(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                setProgram(data.program);
                log('data.program', data.program)

            } else {
                alert(data.message);
            }
        } else {
            alert(message);
        }
    };

    const getAvailableClassPasses = async () => {
        if(!user) {
            return;
        }

        const response =  await UserClassPassService.getUserAvailableClassPassForProgram(id, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;

        // @ts-ignore
        if (response.ok) {
            if (data) {
                setAvailableClassPasses(data.availableClassPasses);

            } else {
                alert(data.message);
            }
        } else {
            alert(message);
        }
    };

    const getProgramPricing = async () => {
        // setIsLoading(true);

        const response =  await ProgramService.getProgramPricing(id, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        // setIsLoading(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                setProgramPricing(data.programPricing);
                log('data.programPricing', data.programPricing);

            } else {
                alert(data.message);
            }
        } else {
            alert(message);
        }
    };

    const purchaseClassPass = async () => {
        trackButtonClickEvent(`purchase class pass`)
        setIsPurchasing(true);

        const response = await TransactionsService.purchaseClassPass(selectedProgramPrice.uid, quantity, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        setIsPurchasing(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                trackPurchaseInitiatedEvent(program?.name, 'class_pass')
                onCloseBuyClassPassDialog();
                setIsStripeCheckoutDialogOpen(true);
                setStripeCheckoutOptions({
                    clientSecret: data.stripeClientSecret
                });

            } else {
                alert(data.message);
            }
        } else {
            alert(message);
        }
    };

    const bookClass = async () => {
        trackButtonClickEvent(`book class`)
        displayScheduleDialog(false)
        setIsBookingClass(true);

        const response =  await BookingsService.bookClass(selectedSchedule.uid, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        setIsBookingClass(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                if (data.insufficientClassPasses) {
                    trackBookingInsufficientClassPassesEvent(program?.name);
                    await displayAlertDialog(t, 'app.insufficientClassPassesLabel', 'error');
                }
                else if (data.isProgramSubscriptionOnHold) {
                    trackBookingProgramSubscriptionOnHoldEvent(program?.name);
                    await displayAlertDialog(t, 'app.classBookingFailedDueToSubscriptionOnHold', 'error');
                }
                else {
                    if (data.isExistingBooking) {
                        trackBookingExistsEvent(program?.name);
                        await displayAlertDialog(t, 'app.classAlreadyBookedLabel', 'info');

                    }
                    else {
                        trackBookingSuccessfulEvent(program?.name)
                        await displayAlertDialog(t, 'app.bookingSuccessfulLabel', 'success');
                        await getAvailableClassPasses();
                    }
                }

            } else {
                trackRequestFailedEvent('booking')
                alert(data.message);
            }
        } else {
            trackRequestFailedEvent('booking')
            alert(message);
        }
    }

    const fetchPurchaseClassPassTransactionStatus = async (stripeCheckoutSessionId) => {
        setIsLoading(true);

        const response = await TransactionsService.fetchTransactionStatus(stripeCheckoutSessionId, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        setIsLoading(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                if (data.status === 'complete') {
                    trackPurchaseSuccessfulEvent(program?.name, 'class_pass');
                    await displayAlertDialog(t, 'app.classPassPurchaseSuccessMessageLabel', 'success');
                }
                else {
                    trackRequestFailedEvent(program?.name, 'class_pass')
                    await displayAlertDialog(t, 'app.paymentFailedMessageLabel', 'error');
                }
                await getAvailableClassPasses();

            } else {
                trackRequestFailedEvent('fetch_purchase_class_pass_transaction_status')
                alert(data.message);
            }
        } else {
            trackRequestFailedEvent('fetch_purchase_class_pass_transaction_status')
            alert(message);
        }
    };

    const getProgramSchedule = async () => {
        // setIsLoading(true);

        const response =  await ProgramService.getProgramSchedule(id, user?.token);
        // @ts-ignore
        const resJson = await response.json();

        const {data, message} = resJson;
        // setIsLoading(false);

        // @ts-ignore
        if (response.ok) {
            if (data) {
                const calSchedules = [];
                for (const schedule of data.programSchedule) {
                    // console.log('programs', programs)
                    const scheduleProgram = program;
                    calSchedules.push({
                        ...schedule,
                        start: new Date(schedule.startTime),
                        end: new Date(schedule.endTime),
                        title: scheduleProgram?.name,
                    });
                }
                setProgramSchedule(data.programSchedule);
                setCalendarSchedules(calSchedules);


            } else {
                alert(data.message);
            }
        } else {
            alert(message);
        }
    };


    const handleSelectSchedule = (schedule) => {
        setSelectedSchedule(schedule);
        displayScheduleDialog(true)
    };

    return (

        <React.Fragment>

            <Box>

                {isLoading && (
                    <>
                        <Skeleton/>
                        <Skeleton/>
                        <Skeleton/>
                        <Skeleton/>
                        <Skeleton/>
                        <Skeleton/>

                    </>
                )}

                {!isLoading && program && (
                    <>
                    <Grid container mb={3} >
                        <Grid item  sm={12} lg={8}>
                            <Button
                                onClick={() => navigate(-1)}
                                size={"small"}
                                startIcon={<ArrowBackIcon />}
                                sx={{marginLeft: -2}}
                            >
                                {t("app.backButtonLabel")}
                            </Button>
                            <ListItem sx={{marginLeft: -3}}>
                                {program?.image && (
                                    <ListItemAvatar sx={{mr: 2, width: 120, overflow: "hidden", borderRadius: 2}}>
                                        <img
                                            width={"120"}
                                            height={"84"}
                                            alt={""}
                                            style={{verticalAlign: 'middle'}}
                                            src={program.image}
                                        />
                                    </ListItemAvatar>
                                )}
                                <ListItemText
                                    primary={
                                        <Typography component={"div"}>
                                            <Typography variant="h1" mb={.25}>
                                                {appLocale.locale === LOCALE_DE ? program?.name : program?.name_en}
                                            </Typography>
                                            {program?.type === PROGRAM_TYPE_FIXED_COURSE && programSchedule && (
                                                <Typography variant={'h5'} mb={3}>
                                                    {dayjs(programSchedule[programSchedule.length - 1]?.startTime).format('MMMM D, YYYY')} - {dayjs(programSchedule[0]?.startTime).format('MMMM D, YYYY')}
                                                </Typography>
                                            )}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography component={"div"}>
                                            {user && (
                                                <Typography variant='subtitle2'>
                                                    {t('app.numberOfAvailableClassCredits')}: {availableClassPasses}
                                                </Typography>
                                            )}

                                            <Typography variant='subtitle2'>
                                                {t('app.languageLabel')}: {getSupportedLocale(program.locale)?.localizedLanguage}
                                            </Typography>
                                            <Typography variant='body1'>
                                                {appLocale.locale === LOCALE_DE ? program.description : program.description_en}
                                            </Typography>

                                        </Typography>
                                    }
                                />
                            </ListItem>

                        </Grid>
                        <Grid item  xs={12} sm={12} md={12} lg={4}>
                            {program.introVideo && (
                                <Box>
                                    <ListItem sx={{marginLeft: -2}}>
                                        <ListItemIcon>
                                            <OndemandVideoIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={{marginLeft: -3}}
                                            primary={
                                                <Typography variant={'body1'}
                                                            sx={{fontWeight: 'bold'}}>
                                                    {t('app.classIntroLabel')}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <Card sx={{ maxWidth: '100%' }}>
                                        <CardActionArea>
                                            <Vimeo
                                                video={program.introVideo}
                                                autopause
                                                showByline={false}
                                                showTitle={false}
                                                autopip
                                                pip
                                                responsive={true}
                                                onPlay={() => {
                                                    trackClassIntroVideoPlayedEvent(program?.name)
                                                }}
                                                onError={() => {
                                                    trackVideoErrorEvent(program?.name, null)
                                                }}
                                            />
                                        </CardActionArea>
                                    </Card>
                                </Box>
                            )}
                        </Grid>
                    </Grid>

                    </>
                )}


            </Box>

            <Divider/>

            <Box mt={2}>

                <Box>

                    {!isLoading && !program && (

                        <Card>
                            <CardContent>
                                <Box sx={{ justifyContent: 'center',  display: 'flex'  }}>
                                    <Typography variant={'h4'}>
                                        {t('app.classDoesntExistLabel')}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {!isLoading && program && (
                        <>
                            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
                                <Tab icon={<CalendarMonth />} iconPosition="start"  label={t('app.scheduleLabel')} />
                                <Tab icon={<CreditCard />} iconPosition="start"  label={t('app.classPassesLabel')} />
                            </Tabs>

                            {tabValue === 1 && (
                                <Grid container spacing={3.75} mb={3}>
                                    {programPricing.map( (programPrice, index) =>
                                        (
                                            <>
                                                <Grid item key={index}>
                                                    <Card sx={{ width: 345 }} onClick={() => {openBuyClassPassDialog(programPrice)}}>
                                                        <CardActionArea>
                                                            <CardMedia
                                                                component="img"
                                                                height="120"
                                                                image={program?.image ? program.image : "https://drehmomentpole.de/wp-content/uploads/2023/11/polo.webp"}
                                                                alt={programPrice.name}
                                                            />
                                                            <CardContent sx={{height: 150}}>
                                                                <Typography gutterBottom variant="h3" component="div">
                                                                    {programPrice.type === PRICING_TYPE_ONE_TIME && (
                                                                        <>€{programPrice.totalAmount}</>
                                                                    )}
                                                                    {programPrice.type === PRICING_TYPE_INSTALMENTAL && (
                                                                        <>
                                                                            €{programPrice.amount}/{t('app.monthLabel')}
                                                                            <Typography variant={'subtitle2'}>
                                                                                {programPrice.paymentIntervals} {t('app.monthSubLabel')}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </Typography>
                                                                <Typography gutterBottom variant="h5" component="div">
                                                                    {appLocale.locale === LOCALE_DE ? programPrice.name : programPrice.name_en}
                                                                </Typography>
                                                                <Typography variant="h5" color="text.secondary">
                                                                    {/*{appLocale.locale === LOCALE_DE ? programPrice.description : programPrice.description_en}*/}

                                                                    {appLocale.locale === LOCALE_DE ? (
                                                                        programPrice.description.length > 100
                                                                            ? `${programPrice.description.substring(0, 97)}...`
                                                                            : programPrice.description
                                                                    ) : (
                                                                        programPrice?.description_en
                                                                            ? (programPrice?.description_en?.length > 100
                                                                                ? `${programPrice?.description_en.substring(0, 97)}...`
                                                                                : programPrice?.description_en)
                                                                            : (programPrice?.description?.length > 100
                                                                                ? `${programPrice?.description.substring(0, 97)}...`
                                                                                : programPrice?.description)
                                                                    )}
                                                                </Typography>
                                                            </CardContent>
                                                        </CardActionArea>
                                                        <CardActions>
                                                            <Button onClick={() => {openBuyClassPassDialog(programPrice)}} variant={'contained'} size="small" color="primary">
                                                                {t('app.buyLabel')}
                                                            </Button>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            </>
                                        )
                                    )}

                                </Grid>

                            )}

                            {tabValue === 0 && (
                                <Box>
                                    <Card>
                                        <CardContent>
                                                <Calendar
                                                    localizer={localizer}
                                                    events={calendarSchedules}
                                                    selectable
                                                    defaultView={isNotMobile ? 'week' : 'day'}
                                                    culture={appLocale.locale === LOCALE_DE ? 'de' : 'en'}
                                                    scrollToTime={scrollCalendarTo12PM}
                                                    onSelectEvent={event => handleSelectSchedule(event)}
                                                    style={{height: 600}}
                                                />
                                        </CardContent>
                                    </Card>
                                </Box>

                            )}

                        </>
                    )}
                </Box>
            </Box>

            <Dialog open={isBuyClassPassDialogOpen} onClose={onCloseBuyClassPassDialog} maxWidth={'xl'} fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{t('app.purchaseClassPassLabel')}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <img
                                src={program?.image}
                                alt={selectedProgramPrice?.name}
                                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h1" gutterBottom>
                                {appLocale.locale === LOCALE_DE ? selectedProgramPrice?.name : selectedProgramPrice?.name_en}
                            </Typography>
                            {program?.type === PROGRAM_TYPE_FIXED_COURSE && programSchedule && (
                                <Typography variant={'h5'} mb={3}>
                                    {dayjs(programSchedule[programSchedule.length - 1]?.startTime).format('MMMM D, YYYY')} - {dayjs(programSchedule[0]?.startTime).format('MMMM D, YYYY')}
                                </Typography>
                            )}
                            <Typography variant="h3" gutterBottom>
                                {selectedProgramPrice?.type === PRICING_TYPE_ONE_TIME && (
                                    <>€{selectedProgramPrice?.totalAmount}</>
                                )}
                                {selectedProgramPrice?.type === PRICING_TYPE_INSTALMENTAL && (
                                    <>
                                        €{selectedProgramPrice?.amount}/{t('app.monthLabel')}
                                        <Typography variant={'subtitle2'}>
                                            {selectedProgramPrice?.paymentIntervals} {t('app.monthSubLabel')}
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {appLocale.locale === LOCALE_DE ? selectedProgramPrice?.description : selectedProgramPrice?.description_en}
                            </Typography>

                            {program?.type === PROGRAM_TYPE_DROP_IN && selectedProgramPrice?.type === PRICING_TYPE_ONE_TIME && (
                                <TextField
                                    label="Quantity"
                                    type="number"
                                    value={quantity}
                                    onChange={handleQuantityChange}
                                    inputProps={{ min: 1 }}
                                    fullWidth
                                    margin="normal"
                                />
                            )}

                            <Box
                                position={'relative'} top={'25%'}
                            >
                                <Typography variant="h3" color="primary" mt={2}>
                                    {t('app.totalPriceLabel')}: €{selectedProgramPriceTotalPrice}
                                </Typography>
                            </Box>


                            <Box
                                position={'relative'} top={'25%'}
                            >
                                <Box>
                                    <Typography variant={"body1"} fontSize={12}>
                                        *{t('app.byPurchasingThisItemLabel')}
                                        <Link
                                            ml={1}
                                            href={"https://drehmomentpole.de/agbs/"}
                                            color={"inherit"}
                                            target={'_blank'}
                                        >{t('app.termsAndConditionsLabel')}</Link>
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant={"body1"} fontSize={12}>
                                        <Box>
                                            <Typography variant={"body1"} fontSize={12}>
                                                **{
                                                appLocale.locale === LOCALE_DE ?
                                                    program?.cancellationPolicy || '' :
                                                    program?.cancellationPolicy_en || ''}
                                            </Typography>
                                        </Box>
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseBuyClassPassDialog} color="primary">
                        {t('app.cancelButtonLabel')}
                    </Button>

                    {user && selectedProgramPrice?.type === PRICING_TYPE_ONE_TIME &&(
                        <LoadingButton loading={isPurchasing} onClick={purchaseClassPass} variant="contained" color="primary">
                            {t('app.payLabel')} €{selectedProgramPriceTotalPrice}
                        </LoadingButton>
                    )}

                    {user && selectedProgramPrice?.type === PRICING_TYPE_INSTALMENTAL &&(
                        <LoadingButton loading={isPurchasing} onClick={purchaseClassPass} variant="contained" color="primary">
                            {t('app.subscribeForLabel')} €{selectedProgramPrice?.amount}/{t('app.monthLabel')}
                        </LoadingButton>
                    )}

                    {!user && (
                        <Button onClick={() => {
                            trackButtonClickEvent(`login to buy class pass`)
                            goToLogin()
                        }} variant="contained" color="primary">
                            {t('app.loginToBuyLabel')}
                        </Button>
                    )}

                </DialogActions>
            </Dialog>

            <Dialog fullScreen={true} open={isStripeCheckoutDialogOpen} onClose={onCloseStripeCheckoutDialog} maxWidth={'xl'} fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h1">{t('app.purchaseClassPassLabel')}</Typography>
                        <Button onClick={onCloseStripeCheckoutDialog} color="primary">
                            {t('app.cancelButtonLabel')}
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {stripeCheckoutOptions.clientSecret !== "" && (
                        <Box>
                            <EmbeddedCheckoutProvider
                                stripe={stripePromise}
                                options={stripeCheckoutOptions}
                            >
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </Box>

                    )}
                </DialogContent>
            </Dialog>


            <Dialog
                sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
                maxWidth="xs"
                open={isScheduleDialogOpen}
            >
                <DialogContent>
                    <Typography variant={'h1'}>
                        <CircleIcon  sx={{fontSize: 16, paddingRight: '2px', paddingTop: '3px'}} color={'info'} />
                        {selectedSchedule?.title}
                    </Typography>
                    <Typography variant={'h5'}>
                        <CalendarMonthIcon sx={{fontSize: 16 , paddingRight: '2px', paddingTop: '3px'}} color={'action'}/>
                        {dayjs(selectedSchedule?.start).format('dddd, MMMM D')} {dayjs(selectedSchedule?.start).format('HH:mm')} - {dayjs(selectedSchedule?.end).format('HH:mm')}
                    </Typography>

                    <Box mt={5}>
                        <Typography variant={"body1"} fontSize={12}>
                            *{t('app.byBookingThisClassLabel')}
                            <Link
                                ml={1}
                                href={"https://drehmomentpole.de/agbs/"}
                                color={"inherit"}
                                target={'_blank'}
                            >{t('app.termsAndConditionsLabel')}</Link>
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant={"body1"} fontSize={12}>
                            **{
                                appLocale.locale === LOCALE_DE ?
                                    program?.cancellationPolicy || '' :
                                    program?.cancellationPolicy_en || ''
                        }
                        </Typography>
                    </Box>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        trackButtonClickEvent(`cancel book class: ${program.name}`)
                        displayScheduleDialog(false)
                        setSelectedSchedule(null);

                    }}>
                        {t('app.cancelLabel')}
                    </Button>

                    {user && (
                        <LoadingButton loading={isBookingClass} onClick={bookClass} variant="contained" color="primary">
                            {t('app.bookClassLabel')}
                        </LoadingButton>
                    )}

                    {!user && (
                        <Button onClick={() => {
                            trackButtonClickEvent(`login to book class`)
                            goToLogin();
                        }} variant="contained" color="primary">
                            {t('app.loginToBookClassLabel')}
                        </Button>
                    )}

                </DialogActions>
            </Dialog>



        </React.Fragment>
    );
}

export default Program;